import { useState, useEffect, useMemo } from 'react'
import { X, Download, ExternalLink, ArrowUpDown, AlertTriangle, Info } from 'lucide-react'
import { presalesDashboardApi } from '@/api/client'
import type { KR2DealsResponse } from '@/api/types'

interface KR2DealDetailProps {
  isOpen: boolean
  onClose: () => void
}

type SortColumn = 'deal_name' | 'presales_engineer' | 'presales_engagement_date' | 'close_date' | 'days_to_close' | 'amount'

export function KR2DealDetailModal({ isOpen, onClose }: KR2DealDetailProps) {
  const [data, setData] = useState<KR2DealsResponse | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [sortBy, setSortBy] = useState<SortColumn>('days_to_close')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc')

  useEffect(() => {
    if (isOpen) {
      setIsLoading(true)
      setError(null)
      presalesDashboardApi.getKR2Deals({ period: 'all_time' })
        .then(res => {
          if (res.success && res.data) {
            setData(res.data)
          } else {
            setError(res.error || 'Failed to load deal data')
          }
        })
        .catch(err => setError(err.message || 'Failed to load deal data'))
        .finally(() => setIsLoading(false))
    }
  }, [isOpen])

  const sortedDeals = useMemo(() => {
    if (!data?.deals) return []
    return [...data.deals].sort((a, b) => {
      let comparison = 0
      switch (sortBy) {
        case 'deal_name':
          comparison = (a.deal_name || '').localeCompare(b.deal_name || '')
          break
        case 'presales_engineer':
          comparison = (a.presales_engineer || a.solutions_consultant_owner || '').localeCompare(
            b.presales_engineer || b.solutions_consultant_owner || ''
          )
          break
        case 'presales_engagement_date':
          comparison = (a.presales_engagement_date || '').localeCompare(b.presales_engagement_date || '')
          break
        case 'close_date':
          comparison = (a.close_date || '').localeCompare(b.close_date || '')
          break
        case 'days_to_close':
          comparison = (a.days_to_close ?? 9999) - (b.days_to_close ?? 9999)
          break
        case 'amount':
          comparison = (a.amount ?? 0) - (b.amount ?? 0)
          break
      }
      return sortDir === 'asc' ? comparison : -comparison
    })
  }, [data, sortBy, sortDir])

  const handleSort = (column: SortColumn) => {
    if (sortBy === column) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc')
    } else {
      setSortBy(column)
      setSortDir('desc')
    }
  }

  const exportCSV = () => {
    if (!data?.deals) return
    const headers = ['Deal Name', 'Company', 'Presales Engineer', 'Solutions Consultant', 'Engagement Date', 'Close Date', 'Days', 'Amount', 'Deal Type', 'Missing Data']
    const rows = sortedDeals.map(d => [
      d.deal_name,
      d.company_name || '',
      d.presales_engineer || '',
      d.solutions_consultant_owner || '',
      d.presales_engagement_date || '',
      d.close_date || '',
      d.days_to_close ?? '',
      d.amount ?? '',
      d.dealtype || '',
      d.missing_engagement_date ? 'Yes' : 'No',
    ])
    const csv = [headers, ...rows].map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')).join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'kr2-deal-verification.csv'
    a.click()
    URL.revokeObjectURL(url)
  }

  const formatDate = (dateStr: string | null | undefined) => {
    if (!dateStr) return '—'
    try {
      return new Date(dateStr).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })
    } catch {
      return dateStr
    }
  }

  const formatAmount = (amount: number | null) => {
    if (amount === null || amount === undefined) return '—'
    return `$${amount.toLocaleString()}`
  }

  if (!isOpen) return null

  const dealsWithDates = data?.deals?.filter(d => d.days_to_close !== null && !d.missing_engagement_date).length ?? 0

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/60 z-40"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="w-full max-w-7xl max-h-[85vh] bg-[var(--bg-secondary)] rounded-lg shadow-modal flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-3 border-b border-[var(--border-color)] shrink-0">
            <div>
              <h2 className="text-lg font-semibold text-[var(--text-primary)]">
                KR2 Deal Verification
              </h2>
              {data?.summary && (
                <div className="text-sm text-[var(--text-secondary)] mt-0.5">
                  <p>
                    <span className="font-medium text-[var(--text-primary)]">{data.summary.with_engagement_date}</span> deals with dates
                    {' '}&middot;{' '}
                    avg <span className="font-medium text-[var(--text-primary)]">{data.summary.avg_days}d</span>
                    {' '}&middot;{' '}
                    median <span className="font-medium text-[var(--text-primary)]">{data.summary.median_days}d</span>
                    {data.summary.missing_engagement_date > 0 && (
                      <>
                        {' '}&middot;{' '}
                        <span className="text-yellow-400">
                          {data.summary.missing_engagement_date} missing dates
                        </span>
                      </>
                    )}
                  </p>
                  <p className="text-xs mt-0.5 flex items-center gap-1">
                    <Info className="h-3 w-3" />
                    {dealsWithDates > 0 ? (
                      <span className="text-green-400">{dealsWithDates} deals with engagement &rarr; close data</span>
                    ) : (
                      <span className="text-[var(--text-muted)]">No deals with complete date data yet</span>
                    )}
                  </p>
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={exportCSV}
                disabled={!data?.deals?.length}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-[var(--text-secondary)] hover:text-[var(--text-primary)] bg-[var(--bg-tertiary)] hover:bg-[var(--bg-primary)] rounded-md transition-colors disabled:opacity-40"
              >
                <Download className="h-3.5 w-3.5" />
                Export CSV
              </button>
              <button
                onClick={onClose}
                className="p-1 text-[var(--text-secondary)] hover:text-[var(--text-primary)] rounded-md hover:bg-[var(--bg-tertiary)] transition-colors"
              >
                <X size={20} />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="overflow-auto flex-1 p-1">
            {isLoading ? (
              <div className="flex items-center justify-center py-16">
                <div className="text-[var(--text-muted)]">Loading deal data...</div>
              </div>
            ) : error ? (
              <div className="flex items-center justify-center py-16">
                <div className="text-red-400">{error}</div>
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-[var(--bg-tertiary)] z-10">
                  <tr>
                    <SortableHeader label="Deal Name" column="deal_name" current={sortBy} dir={sortDir} onSort={handleSort} />
                    <SortableHeader label="Engineer" column="presales_engineer" current={sortBy} dir={sortDir} onSort={handleSort} />
                    <SortableHeader label="Engagement Date" column="presales_engagement_date" current={sortBy} dir={sortDir} onSort={handleSort} />
                    <SortableHeader label="Close Date" column="close_date" current={sortBy} dir={sortDir} onSort={handleSort} />
                    <SortableHeader label="Days" column="days_to_close" current={sortBy} dir={sortDir} onSort={handleSort} />
                    <SortableHeader label="Amount" column="amount" current={sortBy} dir={sortDir} onSort={handleSort} />
                    <th className="text-center px-3 py-2.5 text-xs font-semibold text-[var(--text-secondary)]">
                      HubSpot
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[var(--border-color)]">
                  {sortedDeals.map((deal) => {
                    return (
                      <tr
                        key={deal.hubspot_deal_id}
                        className={`hover:bg-[var(--bg-secondary)] transition-colors ${deal.missing_engagement_date ? 'opacity-60' : ''}`}
                      >
                        <td className="px-3 py-2 font-medium text-[var(--text-primary)] max-w-[220px] truncate">
                          <div className="flex items-center gap-1.5">
                            {deal.missing_engagement_date && (
                              <AlertTriangle className="h-3.5 w-3.5 text-yellow-400 shrink-0" />
                            )}
                            <span className="truncate" title={deal.deal_name}>{deal.deal_name}</span>
                          </div>
                          {deal.company_name && (
                            <div className="text-xs text-[var(--text-muted)] truncate">{deal.company_name}</div>
                          )}
                        </td>
                        <td className="px-3 py-2 text-[var(--text-secondary)]">
                          {deal.presales_engineer || deal.solutions_consultant_owner || '—'}
                        </td>
                        <td className="px-3 py-2 text-[var(--text-secondary)]">
                          {deal.missing_engagement_date ? (
                            <span className="text-yellow-400 text-xs">Missing</span>
                          ) : (
                            formatDate(deal.presales_engagement_date)
                          )}
                        </td>
                        <td className="px-3 py-2 text-[var(--text-secondary)]">
                          {formatDate(deal.close_date)}
                        </td>
                        <td className="px-3 py-2 text-center font-medium">
                          {deal.days_to_close !== null ? (
                            <span className="text-[var(--text-primary)]">{deal.days_to_close}d</span>
                          ) : (
                            <span className="text-[var(--text-muted)]">—</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right text-[var(--text-secondary)]">
                          {formatAmount(deal.amount)}
                        </td>
                        <td className="px-3 py-2 text-center">
                          <a
                            href={`https://app.hubspot.com/contacts/8836420/deal/${deal.hubspot_deal_id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-400 hover:text-blue-300"
                          >
                            <ExternalLink className="h-3.5 w-3.5 inline" />
                          </a>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </>
  )
}

function SortableHeader({
  label,
  column,
  current,
  dir,
  onSort,
}: {
  label: string
  column: SortColumn
  current: SortColumn
  dir: 'asc' | 'desc'
  onSort: (col: SortColumn) => void
}) {
  const isActive = current === column
  return (
    <th
      className="text-left px-3 py-2.5 text-xs font-semibold text-[var(--text-secondary)] cursor-pointer hover:text-[var(--text-primary)] select-none"
      onClick={() => onSort(column)}
    >
      <div className="flex items-center gap-1">
        {label}
        <ArrowUpDown className={`h-3 w-3 ${isActive ? 'text-blue-400' : 'text-[var(--text-muted)]'}`} />
        {isActive && <span className="text-[10px] text-blue-400">{dir === 'asc' ? '↑' : '↓'}</span>}
      </div>
    </th>
  )
}
